import { ReactNode } from "react";
import { DatasetStatusEnum, EDatasetStatus } from "types";
import styles from "../style.module.scss";
import { Typography } from "antd";
import { useReindex } from "hooks/useReindex";
import ReindexModal from "views/components/ContainerHeader/ReindexModal";
import { useVisibility } from "hooks/useVisibility";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { NEW_STATUS } from "../../../../helpers/constants/filters";

interface DisabledLabelPropagationProps {
  deprecatedDatasetStatus: DatasetStatusEnum;
  datasetStatus: EDatasetStatus;
  datasetId: string;
}

export default function DisabledLabelPropagation({
  deprecatedDatasetStatus,
  datasetStatus,
  datasetId,
}: DisabledLabelPropagationProps) {
  const { isLoading } = useReindex(datasetId);
  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;

  const {
    isOpen: isReindexModalOpen,
    open: openReindexModal,
    close: closeReindexModal,
  } = useVisibility();

  const DefaultDisabledMessage = (
    <div>
      This feature isn't available for{" "}
      {!displayNewStatus
        ? deprecatedDatasetStatus
        : datasetStatus.toLowerCase()}{" "}
      dataset.
    </div>
  );

  const ProgressingDisabledMessage = (
    <div>
      This feature isn't available for dataset during{" "}
      {!displayNewStatus
        ? deprecatedDatasetStatus
        : datasetStatus.toLowerCase()}
      .
    </div>
  );

  const { Link } = Typography;

  const PendingIndexDisabledMessage = (
    <div>
      This feature isn't available for{" "}
      {!displayNewStatus
        ? deprecatedDatasetStatus
        : datasetStatus.toLowerCase()}{" "}
      dataset. You'll need to re-index the dataset. You can{" "}
      <Link onClick={openReindexModal} disabled={isLoading}>
        do it now
      </Link>{" "}
      or wait if more data is coming
      {isLoading && " (starting reindex...)"}
    </div>
  );
  const deprecatedStatusMessages: Record<DatasetStatusEnum, ReactNode | null> =
    {
      UPLOADING: ProgressingDisabledMessage,
      UPDATING: ProgressingDisabledMessage,
      ENRICHING: ProgressingDisabledMessage,
      INDEXING: ProgressingDisabledMessage,
      FATAL_ERROR: DefaultDisabledMessage,
      INITIALIZING: ProgressingDisabledMessage,
      READY: null,
      NEW: DefaultDisabledMessage,
      PRE_PROCESSING: ProgressingDisabledMessage,
      SAVING: ProgressingDisabledMessage,
      PENDING_INDEX: PendingIndexDisabledMessage,
    };

  const statusMessages: Record<EDatasetStatus, ReactNode | null> = {
    [EDatasetStatus.READY]: null,
    [EDatasetStatus.INDEXING]: ProgressingDisabledMessage,
    [EDatasetStatus.DRAFT]: DefaultDisabledMessage,
    [EDatasetStatus.ERROR]: DefaultDisabledMessage,
    [EDatasetStatus.PARTIAL_INDEX]: PendingIndexDisabledMessage,
    [EDatasetStatus.READ_ONLY]: DefaultDisabledMessage,
  };

  const message = displayNewStatus
    ? statusMessages[datasetStatus]
    : deprecatedStatusMessages[deprecatedDatasetStatus];

  if (!message) {
    return null;
  }

  return (
    <>
      <div className={styles.notEnabled}>{message}</div>
      <ReindexModal open={isReindexModalOpen} onClose={closeReindexModal} />
    </>
  );
}
